import {
  BaseProduct,
  IncludedTokensConfig,
  ProductConfig,
} from "@utils/product/product-utils-types";
import { AllCustomProductsTypes } from "@utils/product/product-types";
import { capitalize } from "es-toolkit/string";
import { CREDITS_DISPLAY_NAME } from "@utils/limit-data-display";
import { createLimitPayload } from "@utils/limit-utils";
import { createProductsFromConfigs } from "@utils/product/product-utils";

/**
 * Enterprise plans take their storage from the tier selection instead of a text field, so the
 * limit starts at 0 and stays required until a tier is picked. Active Users is offered on top
 * of it as an optional limit, which is why it is not part of the defaults.
 */
const enterpriseLimits: ProductConfig["scopesWithRequiredDefaultsLimit"] = {
  COMPANY: createLimitPayload([{ type: "STORAGE", limit: 0 }]),
};

/**
 * The tokens the 2026 Scan plans include per year. Free and Starter include none, and the number
 * is locked there so that it cannot be raised by mistake; the paid plans start at the number they
 * are sold with, and an admin may customize it.
 */
const includedTokensPerYear = {
  scanFree2026: { defaultPerYear: 0, isFixed: true },
  scanStarter2026: { defaultPerYear: 0, isFixed: true },
  scanEssential2026: { defaultPerYear: 4000, isFixed: false },
  scanProfessional2026: { defaultPerYear: 12000, isFixed: false },
  scanEnterprise2026: { defaultPerYear: 24000, isFixed: false },
} as const satisfies Record<string, IncludedTokensConfig>;

/** A package of tokens that an admin can add to a workspace */
interface TokenPackage {
  /** The name of the package */
  name: string;

  /** The amount of tokens in the package */
  value: number;
}

/**
 * Packages of tokens that an admin can add to a workspace
 */
const tokenPackages = {
  small: { name: "Small $1 000", value: 10_000 },
  medium: { name: "Medium $4 750", value: 50_000 },
  large: { name: "Large $9 000", value: 100_000 },
} as const satisfies Record<string, TokenPackage>;

const customs: Record<AllCustomProductsTypes, ProductConfig> = {
  standard360: {
    identifiers: ["sphere-xg-standard", "sphere-xg-addon-360"],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "PROJECT", limit: 3 },
        { type: "STORAGE", limit: 2.5 },
        { type: "USER", limit: 3 },
      ]),
    },
    name: "360° Standard",
    isCoreProduct: true,
    apps: ["holoBuilder", "sphereXg"],
  },
  standardPointCloud: {
    // These plans offer no application choice, so the dashboard is fixed by the product itself
    identifiers: [
      "sphere-xg-standard",
      "sphere-xg-addon-point-cloud",
      "sphere-dashboard",
    ],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "STORAGE", limit: 2.5 },
        { type: "USER", limit: 3 },
      ]),
    },
    name: "Point Cloud Standard",
    isCoreProduct: true,
    apps: ["sphereXg"],
  },
  pro360: {
    identifiers: ["sphere-xg-pro", "sphere-xg-addon-360"],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        {
          type: "PROJECT",
          limit: 10,
        },
        {
          type: "STORAGE",
          limit: 5,
        },
        { type: "USER", limit: 10 },
      ]),
    },
    name: "360° Professional",
    isCoreProduct: true,
    apps: ["holoBuilder", "sphereXg"],
  },
  proPointCloud: {
    identifiers: [
      "sphere-xg-pro",
      "sphere-xg-addon-point-cloud",
      "sphere-dashboard",
    ],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "STORAGE", limit: 5 },
        { type: "USER", limit: 10 },
      ]),
    },
    name: "Point Cloud Professional",
    isCoreProduct: true,
    apps: ["sphereXg"],
  },

  enterprise360: {
    identifiers: ["sphere-xg-enterprise", "sphere-xg-addon-360"],
    scopesWithRequiredDefaultsLimit: enterpriseLimits,
    name: "360° Enterprise",
    isCoreProduct: true,
    apps: ["holoBuilder", "sphereXg"],
  },
  enterprisePointCloud: {
    identifiers: [
      "sphere-xg-enterprise",
      "sphere-xg-addon-point-cloud",
      "sphere-dashboard",
    ],
    scopesWithRequiredDefaultsLimit: enterpriseLimits,
    name: "Point Cloud Enterprise",
    isCoreProduct: true,
    apps: ["sphereXg"],
  },
  enterprise360PointCloud: {
    identifiers: [
      "sphere-xg-enterprise",
      "sphere-xg-addon-360",
      "sphere-xg-addon-point-cloud",
    ],
    scopesWithRequiredDefaultsLimit: enterpriseLimits,
    name: "Point Cloud & 360° Enterprise",
    isCoreProduct: true,
    apps: ["holoBuilder", "sphereXg"],
  },
  scanFree2026: {
    identifiers: ["sphere-xg-2026-scan-free"],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "ACTIVE_SEAT", limit: 1, isFixed: true },
        { type: "STORAGE", limit: 5, unit: "GB", isFixed: true },
      ]),
    },
    name: "Scan Free 2026",
    isCoreProduct: false,
    apps: ["sphereXg"],
    includedTokens: includedTokensPerYear.scanFree2026,
  },
  scanStarter2026: {
    identifiers: ["sphere-xg-2026-scan-starter"],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "ACTIVE_SEAT", limit: 1, isFixed: true },
        { type: "STORAGE", limit: 20, unit: "GB", isFixed: true },
      ]),
    },
    name: "Scan Starter 2026",
    isCoreProduct: false,
    apps: ["sphereXg"],
    includedTokens: includedTokensPerYear.scanStarter2026,
  },
  scanEssential2026: {
    identifiers: ["sphere-xg-2026-scan-essential"],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "ACTIVE_SEAT", limit: 1, isFixed: true },
        { type: "STORAGE", limit: 1, unit: "TB" },
      ]),
    },
    name: "Scan Essential 2026",
    isCoreProduct: false,
    includedTokens: includedTokensPerYear.scanEssential2026,
    hasSceneSeats: true,
    apps: ["sphereXg"],
  },
  scanProfessional2026: {
    identifiers: ["sphere-xg-2026-scan-professional"],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "ACTIVE_SEAT", limit: 3 },
        { type: "STORAGE", limit: 2, unit: "TB" },
      ]),
    },
    name: "Scan Professional 2026",
    isCoreProduct: false,
    includedTokens: includedTokensPerYear.scanProfessional2026,
    hasSceneSeats: true,
    apps: ["sphereXg"],
  },
  scanEnterprise2026: {
    identifiers: ["sphere-xg-2026-scan-enterprise"],
    scopesWithRequiredDefaultsLimit: {
      COMPANY: createLimitPayload([
        { type: "ACTIVE_SEAT", limit: 5 },
        { type: "STORAGE", limit: 4, unit: "TB" },
      ]),
    },
    name: "Scan Enterprise 2026",
    isCoreProduct: false,
    includedTokens: includedTokensPerYear.scanEnterprise2026,
    hasSceneSeats: true,
    apps: ["sphereXg"],
  },
  addCreditsSmall: createCreditProduct(tokenPackages.small),
  addCreditsMedium: createCreditProduct(tokenPackages.medium),
  addCreditsLarge: createCreditProduct(tokenPackages.large),
  addCredits: {
    scopesWithEditableLimit: ["COMPANY"],
    name: `Add Custom ${capitalize(CREDITS_DISPLAY_NAME)}`,
    description: `Add ${CREDITS_DISPLAY_NAME} to the workspace`,
    // Set to empty array to prevent overwritten by createProductsFromConfigs function
    identifiers: [],
    isCoreProduct: true,
  },
};

/** Creates and returns credit product based on the token package */
function createCreditProduct(tokenPackage: TokenPackage): ProductConfig {
  return {
    // Set to empty array to prevent overwritten by createProductsFromConfigs function
    identifiers: [],
    name: `Add ${tokenPackage.value} ${capitalize(CREDITS_DISPLAY_NAME)} (${
      tokenPackage.name
    })`,
    description: `Add ${tokenPackage.value} ${CREDITS_DISPLAY_NAME} to the workspace`,
    value: tokenPackage.value,
    isCoreProduct: true,
    scopesWithDefaultsHiddenLimit: {
      COMPANY: [
        {
          type: "CREDITS",
          value: tokenPackage.value,
        },
      ],
    },
  };
}

/** List of all available customs in Admin Panel */
export const allAvailableCustomProducts: Record<
  AllCustomProductsTypes,
  BaseProduct
> = createProductsFromConfigs(customs);
